import { useOrganisation } from '@my/directory-api/hooks';
import { FormSection } from '@my/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { NewOrganisationPanel } from './NewOrganisationPanel';
import { OrganisationPanel } from './OrganisationPanel';
import { useAccountContext } from './useAccountContext';

/**
 * The organisations inside this account, all of them, one panel after another.
 *
 * <p>
 * STACKED RATHER THAN PAGED. An account has two or three organisations, so
 * scrolling past one to reach the next is cheaper than a pager: no index to
 * hold, nothing to clamp when the support strip switches customer, and every
 * organisation is findable with Ctrl-F. A pager only pays once the list is long
 * enough that scrolling is the slower option, which this never is.
 * </p>
 *
 * <p>
 * Only the fields every app reads are here. An app's own configuration — tax
 * schemas, nominal codes, sequences, document templates, TMS reference labels —
 * stays in that app's Settings, beside the screens that use it.
 * </p>
 */
export function AccountOrganisationsPage() {
  const { account, headerSlot } = useAccountContext();
  const [isAdding, setIsAdding] = useState(false);

  // Seed a new organisation from the first existing one: a second company
  // under the same customer is almost always in the same country on the same
  // currency, and the server's IE/EUR fallback is wrong for every account that
  // is not Irish.
  const firstId = account.organisations[0]?.id ?? null;
  const first = useOrganisation(firstId).data;

  return (
    <>
      {/* Into the page header rather than a row of its own, which was mostly
          blank space above the first panel. */}
      {headerSlot &&
        createPortal(
          <button
            type="button"
            onClick={() => setIsAdding((v) => !v)}
            className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>,
          headerSlot,
        )}

      {isAdding && (
        <NewOrganisationPanel
          defaults={
            first
              ? {
                  currencyIsoCode: first.currencyIsoCode,
                  countryIsoCode: first.countryIsoCode,
                  operationalTimeZone: first.operationalTimeZone,
                  languageCode: first.languageCode,
                  timePattern: first.timePattern,
                  datePattern: first.datePattern,
                }
              : undefined
          }
          onDone={() => setIsAdding(false)}
        />
      )}

      {account.organisations.length === 0 && !isAdding && (
        <FormSection title="Organisations" columns={1}>
          <p className="text-xs italic text-slate-400">
            No organisations yet — one is needed before any app can be enabled.
          </p>
        </FormSection>
      )}

      {account.organisations.map((org) => (
        // Keyed on the id so each panel owns its own loaded record and draft.
        // Without it React reuses one form across organisations, which is how
        // Dublin's tax number ends up saved onto Belfast.
        <OrganisationPanel key={org.id} organisationId={org.id} />
      ))}
    </>
  );
}
